// alertSlice.ts

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../';
import axios from '../../../services/ApiService'
import { Campaign, Post } from '../../../types/apps/Campaign';

export interface CampaignsState {
    campaigns: Campaign[];
    loading: boolean;
    error: string | null;
}

export interface ChangePostStatusPayload {
    postId: number;
    newStatus: number;
}

const initialState: CampaignsState = {
    campaigns: [],
    loading: false,
    error: null,
};

const fetchCampaigns = async () => {
    try {
        const response = await axios.get<Campaign[]>('campaign/GetCampaigns');
        return response.data; // Return the data from the response
    } catch (error) {
        console.error('An error occurred while fetching the campaigns:', error);
        throw error; // Rethrow the error to handle it in the async thunk
    }
};


export const getCampaigns = createAsyncThunk<Campaign[]>(
    'campaigns/getCampaigns',
    async () => {
        const response = await fetchCampaigns(); // Fetch campaigns from API
        return response;
    }
);

export const selectCampaignsByChannel = (state: RootState, channelId: number): Campaign[] => {
    if (!channelId) {
        // If no channelId is specified, return all campaigns
        return state.campaign.campaigns;
    }

    // Return campaigns with posts that match the specified channelId
    return state.campaign.campaigns.map(campaign => ({
        ...campaign,
        posts: campaign.posts ? campaign.posts.filter(post => post.channelId === channelId) : []
    }));
};


export const updatePostStatus = createAsyncThunk(
    'campaigns/updatePostStatus',
    async ({ postId, newStatus }: { postId: number, newStatus: number }, { getState, rejectWithValue }) => {
        try {
            // Make the API call to update the post status
            await axios.put(`/api/posts/${postId}/status`, { status: newStatus });

            // Get the current state to find the campaign and post
            const state = getState() as RootState;
            const campaigns = state.campaign.campaigns;

            // Find the campaign and post
            let updatedCampaign: Campaign | undefined;
            let updatedPost: Post | undefined;

            for (const campaign of campaigns) {
                if (campaign.posts) {
                    const post = campaign.posts.find(p => p.id === postId);
                    if (post) {
                        updatedCampaign = { ...campaign };
                        updatedPost = { ...post, postStatus: newStatus };
                        break;
                    }
                }
            }

            // Return updated campaign and post
            return { updatedCampaign, updatedPost };
        } catch (error) {
            console.error('Error updating post status:', error);
            return rejectWithValue(error);
        }
    }
);


export const deleteCampaign = createAsyncThunk(
    'campaigns/deleteCampaign',
    async (campaignGuid : string, { dispatch }) => {
        await axios.post(`/campaign/DeleteCampaign?campaignGuid=${campaignGuid}`);
        // Optionally, dispatch another action to refresh the list
        dispatch(getCampaigns()); // Assuming you have a fetchCampaigns action
    }
);

const campaignSlice = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getCampaigns.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCampaigns.fulfilled, (state, action: PayloadAction<Campaign[]>) => {
                state.loading = false;
                state.campaigns = action.payload;
            })
            .addCase(getCampaigns.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch campaigns';
            })
            .addCase(updatePostStatus.fulfilled, (state, action) => {
                const { updatedCampaign, updatedPost } = action.payload;
                if (updatedCampaign && updatedPost) {
                    const campaignIndex = state.campaigns.findIndex(c => c.campaignGuid === updatedCampaign.campaignGuid);
                    if (campaignIndex > -1) {
                        const campaign = state.campaigns[campaignIndex];
                        if (campaign.posts) {
                            const postIndex = campaign.posts.findIndex(p => p.id === updatedPost.id);

                            if (postIndex > -1 && state.campaigns[campaignIndex] && state.campaigns[campaignIndex].posts) {
                                // Now we are sure that campaign and the post both exist
                                if (state.campaigns?.[campaignIndex]?.posts?.[postIndex])
                                    state.campaigns[campaignIndex]!.posts![postIndex] = updatedPost;
                            }
                        }
                    }
                }
            });
    },
});

export default campaignSlice.reducer;

//export const { addTemporaryAlert, removeTemporaryAlert, addAlert, removeAlert } = alertSlice.actions;
//export default alertSlice.reducer;
